import type { DateTimeUnit } from 'luxon';
import { DateTime, Interval } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import 'reselect';

// Note: Unless specified, all timezones will be in local time (whatever that is for a given user)

let startOfToday: DateTime | null = null;
export const getStartOfToday = (now: DateTime) => {
  const today = now.startOf('day');
  if (startOfToday === null || +startOfToday !== +today) {
    startOfToday = today;
  }
  return startOfToday;
};

let endOfToday: DateTime | null = null;
export const getEndOfToday = (now: DateTime) => {
  const today = now.endOf('day');
  if (endOfToday === null || +endOfToday !== +today) {
    endOfToday = today;
  }
  return endOfToday;
};

export const getStartOfDayFromToday = createCachedSelector(
  getStartOfToday,
  (_now: DateTime, relativeValue: number) => relativeValue,
  (_now, _relativeValue, unit = 'days') => unit,
  (currentDay, relativeValue, unit) => currentDay.plus({ [unit]: relativeValue }),
)((_now, relativeValue, unit = 'days') => `${relativeValue}_${unit}`);

let startOfThisMonth: DateTime | null = null;
export const getStartOfThisMonth = (now: DateTime) => {
  const thisMonth = now.startOf('month');
  if (startOfThisMonth === null || +startOfThisMonth !== +thisMonth) {
    startOfThisMonth = thisMonth;
  }
  return startOfThisMonth;
};

let endOfThisMonth: DateTime | null = null;
export const getEndOfThisMonth = (now: DateTime) => {
  const thisMonth = now.endOf('month');
  if (endOfThisMonth === null || +endOfThisMonth !== +thisMonth) {
    endOfThisMonth = thisMonth;
  }
  return endOfThisMonth;
};

export const getEndOfMonthFromThisMonth = createCachedSelector(
  getEndOfThisMonth,
  (_now: DateTime, relativeMonths: number) => relativeMonths,
  (thisMonth, relativeMonths) => thisMonth.plus({ months: relativeMonths }).endOf('month'),
)((_now, relativeMonths) => relativeMonths);

let startOfQuarter: DateTime | null = null;
export const getStartOfQuarter = (now: DateTime) => {
  const thisQuarter = now.startOf('quarter');
  if (startOfQuarter === null || +startOfQuarter !== +thisQuarter) {
    startOfQuarter = thisQuarter;
  }
  return startOfQuarter;
};

export const getStartOfQuarterFromThisQuarter = createCachedSelector(
  getStartOfQuarter,
  (_now: DateTime, relativeQuarters: number) => relativeQuarters,
  (thisQuarter, relativeQuarters) => thisQuarter.plus({ quarters: relativeQuarters }),
)((_now, relativeQuarters) => relativeQuarters);

let endOfQuarter: DateTime | null = null;
export const getEndOfQuarter = (now = DateTime.local()) => {
  const thisQuarter = now.endOf('quarter');
  if (endOfQuarter === null || +endOfQuarter !== +thisQuarter) {
    endOfQuarter = thisQuarter;
  }
  return endOfQuarter;
};

export const getEndOfQuarterFromThisQuarter = createCachedSelector(
  getEndOfQuarter,
  (_now: DateTime, relativeQuarters: number) => relativeQuarters,
  (thisQuarter, relativeQuarters) => thisQuarter.plus({ quarters: relativeQuarters }).endOf('quarter'),
)((_now, relativeQuarters) => relativeQuarters);

export const getStartOfMonthFromThisMonth = createCachedSelector(
  getStartOfThisMonth,
  (_now: DateTime, relativeMonths: number) => relativeMonths,
  (thisMonth, relativeMonths) => thisMonth.plus({ months: relativeMonths }),
)((_now, relativeMonths) => relativeMonths);

let startOfThisYear: DateTime | null = null;
export const getStartOfThisYear = (now: DateTime) => {
  const thisYear = now.startOf('year');
  if (startOfThisYear === null || +startOfThisYear !== +thisYear) {
    startOfThisYear = thisYear;
  }
  return startOfThisYear;
};

export const getEndOfThisYear = (now: DateTime) => now.endOf('year');

export const getStartOfYearFromThisYear = createCachedSelector(
  getStartOfThisYear,
  (_now: DateTime, relativeYears: number) => relativeYears,
  (thisYear, relativeYears) => thisYear.plus({ years: relativeYears }),
)((_now, relativeYears) => relativeYears);

export const getInterval = createCachedSelector(
  (startDate: DateTime) => startDate,
  (_: any, endDate: DateTime) => endDate,
  (startDate, endDate) =>
    startDate <= endDate ? Interval.fromDateTimes(startDate, endDate) : Interval.fromDateTimes(endDate, startDate),
)({
  keySelector: (startDate, endDate) => `${startDate}_${endDate}`,
  cacheObject: new LruObjectCache({ cacheSize: 50 }),
});

export const LEGACY_RECENT_RANGE_MAP = {
  LAST_7_DAYS: 'RECENT_7_DAYS',
  LAST_14_DAYS: 'RECENT_14_DAYS',
  LAST_30_DAYS: 'RECENT_30_DAYS',
  LAST_90_DAYS: 'RECENT_90_DAYS',
  LAST_180_DAYS: 'RECENT_180_DAYS',
  LAST_365_DAYS: 'RECENT_365_DAYS',
  LAST_1825_DAYS: 'RECENT_1825_DAYS',
  LAST_2_MONTHS: 'RECENT_2_MONTHS',
  LAST_3_MONTHS: 'RECENT_3_MONTHS',
  LAST_6_MONTHS: 'RECENT_6_MONTHS',
  LAST_12_MONTHS: 'RECENT_12_MONTHS',
  LAST_5_YEARS: 'RECENT_5_YEARS',
} as const;

export type LegacyRecentRange = keyof typeof LEGACY_RECENT_RANGE_MAP;
export type CanonicalRecentRange = (typeof LEGACY_RECENT_RANGE_MAP)[LegacyRecentRange];
export type LegacyRecentPredefinedRange = Extract<
  LegacyRecentRange,
  'LAST_30_DAYS' | 'LAST_2_MONTHS' | 'LAST_3_MONTHS' | 'LAST_6_MONTHS' | 'LAST_12_MONTHS' | 'LAST_5_YEARS'
>;

export type PREDEFINED_RANGE_VALUE =
  | 'TODAY'
  | 'MONTH_TO_DATE'
  | 'THIS_MONTH'
  | 'YEAR_TO_DATE'
  | 'THIS_YEAR'
  | 'RECENT_30_DAYS'
  | '30_DAYS'
  | '60_DAYS'
  | '90_DAYS'
  | '365_DAYS'
  | 'NEXT_365_DAYS'
  | 'RECENT_2_MONTHS'
  | 'RECENT_3_MONTHS'
  | 'RECENT_6_MONTHS'
  | 'LAST_7_MONTHS'
  | 'RECENT_12_MONTHS'
  | 'LAST_MONTH'
  | 'LAST_YEAR'
  | 'RECENT_5_YEARS'
  | 'NEXT_7_DAYS'
  | 'NEXT_14_DAYS'
  | 'NEXT_MONTH'
  | 'NEXT_30_DAYS'
  | 'NEXT_60_DAYS'
  | 'THIS_QUARTER'
  | 'LAST_QUARTER'
  | 'NEXT_QUARTER'
  | '6_MONTHS'
  | '12_MONTHS'
  | '3_MONTHS_PRIOR'
  | '6_MONTHS_PRIOR'
  | '12_MONTHS_PRIOR'
  | 'QUARTER_TO_DATE'
  | 'LAST_24_MONTHS'
  | 'ALL_DATES'
  | 'SAME_DAY_LAST_MONTH'
  | 'SAME_DAY_LAST_3_MONTHS'
  | 'SAME_DAY_LAST_6_MONTHS'
  | 'SAME_DAY_LAST_YEAR'
  | 'SAME_DAY_LAST_5_YEARS'
  | LegacyRecentPredefinedRange;

// These cannot be in flux module!
// importing flux into our utils breaks tree-shaking for QEM! Don't move to generated reports!
export const GENERATED_REPORT_RANGES = [
  'ALL_DATES',
  'THIS_MONTH',
  'THIS_QUARTER',
  'THIS_YEAR',
  'LAST_MONTH',
  'LAST_QUARTER',
  'LAST_YEAR',
  'RECENT_5_YEARS',
  'MONTH_TO_DATE',
  'QUARTER_TO_DATE',
  'YEAR_TO_DATE',
  'RECENT_7_DAYS',
  'RECENT_14_DAYS',
  'RECENT_30_DAYS',
  'RECENT_90_DAYS',
  'RECENT_180_DAYS',
  'RECENT_365_DAYS',
  'RECENT_1825_DAYS',
  'SAME_DAY_LAST_MONTH',
  'SAME_DAY_LAST_3_MONTHS',
  'SAME_DAY_LAST_6_MONTHS',
  'SAME_DAY_LAST_YEAR',
  'SAME_DAY_LAST_5_YEARS',
  'RECENT_2_MONTHS',
  'RECENT_3_MONTHS',
  'RECENT_6_MONTHS',
  'LAST_7_MONTHS',
  'RECENT_12_MONTHS',
  'CUSTOM',
  'LAST_24_MONTHS',
  'CUSTOM_LAST',
  'CUSTOM_SINCE',
  'CUSTOM_RECENT',
] as const;

export type GeneratedReportRanges = (typeof GENERATED_REPORT_RANGES)[number];
export type GeneratedReportRangeInput = GeneratedReportRanges | LegacyRecentRange;

/**
 * Runtime membership check for `GeneratedReportRanges`.
 *
 * The type is derived from `GENERATED_REPORT_RANGES` so the two cannot drift. Needed because
 * date ranges arrive from untrusted sources — saved configurations written by an older client and
 * server responses that are passed through untyped — where a `as GeneratedReportRanges` cast would
 * wave through a value that no downstream `switch` handles.
 */
export const isGeneratedReportRange = (value: unknown): value is GeneratedReportRanges =>
  typeof value === 'string' && (GENERATED_REPORT_RANGES as readonly string[]).includes(value);

const normalizeRecentRangeId = (value: string): string =>
  Object.prototype.hasOwnProperty.call(LEGACY_RECENT_RANGE_MAP, value)
    ? LEGACY_RECENT_RANGE_MAP[value as LegacyRecentRange]
    : value;

export function normalizeGeneratedReportRange(value: GeneratedReportRangeInput): GeneratedReportRanges;
export function normalizeGeneratedReportRange(value: unknown): GeneratedReportRanges | undefined;
export function normalizeGeneratedReportRange(value: unknown): GeneratedReportRanges | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = normalizeRecentRangeId(value);
  return isGeneratedReportRange(normalized) ? normalized : undefined;
}

export const normalizePredefinedRangeValue = (
  value: PREDEFINED_RANGE_VALUE,
): Exclude<PREDEFINED_RANGE_VALUE, LegacyRecentPredefinedRange> =>
  normalizeRecentRangeId(value) as Exclude<PREDEFINED_RANGE_VALUE, LegacyRecentPredefinedRange>;

export const isGeneratedReportRangeTypeDynamic = (
  dateRange: GeneratedReportRanges,
): dateRange is DynamicRange['type'] =>
  dateRange === 'CUSTOM_LAST' || dateRange === 'CUSTOM_SINCE' || dateRange === 'CUSTOM_RECENT';

// TODO: Replace with QCS generated values
export const GENERATED_REPORT_CHANGE_OVER_TIME_LABELS = {
  ALL_DATES: 'All-time change',
  THIS_MONTH: 'Change this month',
  THIS_QUARTER: 'Quarter to date change',
  THIS_YEAR: 'Change this year',
  LAST_MONTH: 'Change last month',
  LAST_QUARTER: 'Change last quarter',
  LAST_YEAR: 'Change last year',
  RECENT_5_YEARS: '5 year change',
  MONTH_TO_DATE: 'Month to date change',
  QUARTER_TO_DATE: 'Quarter to date change',
  YEAR_TO_DATE: 'Year to date change',
  RECENT_7_DAYS: '7 day change',
  RECENT_14_DAYS: '14 day change',
  RECENT_30_DAYS: '1 month change',
  RECENT_90_DAYS: '3 month change',
  RECENT_180_DAYS: '6 month change',
  RECENT_365_DAYS: '1 year change',
  RECENT_1825_DAYS: '5 year change',
  SAME_DAY_LAST_MONTH: '1 month change',
  SAME_DAY_LAST_3_MONTHS: '3 month change',
  SAME_DAY_LAST_6_MONTHS: '6 month change',
  SAME_DAY_LAST_YEAR: '1 year change',
  SAME_DAY_LAST_5_YEARS: '5 year change',
  RECENT_2_MONTHS: '2 month change',
  RECENT_3_MONTHS: '3 month change',
  RECENT_6_MONTHS: '6 month change',
  LAST_7_MONTHS: '7 month change',
  RECENT_12_MONTHS: '1 year change',
  CUSTOM: 'Custom',
  LAST_24_MONTHS: '2 year change',
  CUSTOM_SINCE: 'Change since custom date',
  CUSTOM_LAST: 'Change over rolling window',
  CUSTOM_RECENT: 'Change from start of period through today',
};

// TODO: Replace with QCS generated values
export const GENERATED_REPORT_CHANGE_OVER_TIME_SHORT_LABELS = {
  ALL_DATES: 'All-time change',
  THIS_MONTH: '1mo change',
  THIS_QUARTER: 'QTD change',
  THIS_YEAR: 'This yr change',
  LAST_MONTH: 'Last mo change',
  LAST_QUARTER: 'Last qtr change',
  LAST_YEAR: 'Last yr change',
  RECENT_5_YEARS: '5yr change',
  MONTH_TO_DATE: 'MTD change',
  QUARTER_TO_DATE: 'QTD change',
  YEAR_TO_DATE: 'YTD change',
  RECENT_7_DAYS: '7d change',
  RECENT_14_DAYS: '14d change',
  RECENT_30_DAYS: '1mo change',
  RECENT_90_DAYS: '3mo change',
  RECENT_180_DAYS: '6mo change',
  RECENT_365_DAYS: '1yr change',
  RECENT_1825_DAYS: '5yr change',
  SAME_DAY_LAST_MONTH: '1mo change',
  SAME_DAY_LAST_3_MONTHS: '3mo change',
  SAME_DAY_LAST_6_MONTHS: '6mo change',
  SAME_DAY_LAST_YEAR: '1yr change',
  SAME_DAY_LAST_5_YEARS: '5yr change',
  RECENT_2_MONTHS: '2mo change',
  RECENT_3_MONTHS: '3mo change',
  RECENT_6_MONTHS: '6mo change',
  LAST_7_MONTHS: '7mo change',
  RECENT_12_MONTHS: '1yr change',
  CUSTOM: 'Custom',
  LAST_24_MONTHS: '2yr change',
  CUSTOM_SINCE: 'Since custom date',
  CUSTOM_LAST: 'Rolling change',
  CUSTOM_RECENT: 'Period change',
};

export type Timestep =
  | 'DAY'
  | 'WEEK'
  | 'TWO_WEEKS'
  | 'HALF_MONTH'
  | 'MONTH'
  | 'QUARTER'
  | 'SIX_MONTHS'
  | 'YEAR'
  | 'NONE'
  | 'OVER-TIME';

export type DynamicRangeIntervalTimestep = Extract<Timestep, 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR'>;

export type CUSTOM_RANGE_VALUE = 'CUSTOM';

export type PredefinedRange =
  | {
      value: CUSTOM_RANGE_VALUE;
      endDate: DateTime;
      startDate: DateTime;
    }
  | {
      value: PREDEFINED_RANGE_VALUE;
    };

export const convertGeneratedReportDateRangeToPredefinedRange = (
  input: GeneratedReportRangeInput,
): PREDEFINED_RANGE_VALUE | undefined => {
  const normalized = normalizeGeneratedReportRange(input);
  switch (normalized) {
    case 'ALL_DATES':
    case 'THIS_MONTH':
    case 'THIS_QUARTER':
    case 'LAST_MONTH':
    case 'LAST_QUARTER':
    case 'LAST_YEAR':
    case 'RECENT_5_YEARS':
    case 'MONTH_TO_DATE':
    case 'QUARTER_TO_DATE':
    case 'YEAR_TO_DATE':
    case 'RECENT_2_MONTHS':
    case 'RECENT_3_MONTHS':
    case 'RECENT_6_MONTHS':
    case 'LAST_7_MONTHS':
    case 'RECENT_12_MONTHS':
    case 'LAST_24_MONTHS':
      return normalized;
  }
  return undefined;
};

export const convertPredefinedRangeToGeneratedReportDateRange = (
  input: PREDEFINED_RANGE_VALUE,
): GeneratedReportRanges | undefined => {
  const normalized = normalizePredefinedRangeValue(input);
  switch (normalized) {
    case 'MONTH_TO_DATE':
    case 'THIS_MONTH':
    case 'YEAR_TO_DATE':
    case 'THIS_YEAR':
    case 'RECENT_2_MONTHS':
    case 'RECENT_3_MONTHS':
    case 'RECENT_6_MONTHS':
    case 'LAST_7_MONTHS':
    case 'RECENT_12_MONTHS':
    case 'LAST_24_MONTHS':
    case 'LAST_MONTH':
    case 'LAST_YEAR':
    case 'RECENT_5_YEARS':
    case 'THIS_QUARTER':
    case 'LAST_QUARTER':
    case 'QUARTER_TO_DATE':
    case 'ALL_DATES':
    case 'SAME_DAY_LAST_MONTH':
    case 'SAME_DAY_LAST_3_MONTHS':
    case 'SAME_DAY_LAST_6_MONTHS':
    case 'SAME_DAY_LAST_YEAR':
    case 'SAME_DAY_LAST_5_YEARS':
      return normalized;
    case '12_MONTHS':
    case '3_MONTHS_PRIOR':
    case '6_MONTHS_PRIOR':
    case '12_MONTHS_PRIOR':
    case 'NEXT_QUARTER':
    case 'NEXT_7_DAYS':
    case 'NEXT_14_DAYS':
    case 'NEXT_MONTH':
    case 'NEXT_30_DAYS':
    case 'NEXT_60_DAYS':
    case '60_DAYS':
    case '90_DAYS':
    case '365_DAYS':
    case 'NEXT_365_DAYS':
    case '30_DAYS':
    case 'RECENT_30_DAYS':
    case 'TODAY':
    case '6_MONTHS':
      // Up to the consumer to figure out what they want to do here. These values are not supported by the generated reports service.
      return undefined;
  }
};

export const constructPredefinedInterval = (predefinedRange: PredefinedRange, now: DateTime): Interval => {
  if (predefinedRange.value === 'CUSTOM') {
    return getInterval(predefinedRange.startDate, predefinedRange.endDate);
  }

  switch (normalizePredefinedRangeValue(predefinedRange.value)) {
    case 'TODAY':
      return getInterval(getStartOfToday(now), getEndOfToday(now));
    case 'MONTH_TO_DATE':
      return getInterval(getStartOfThisMonth(now), getEndOfToday(now));
    case 'THIS_MONTH':
      return getInterval(getStartOfThisMonth(now), getEndOfThisMonth(now));
    case 'YEAR_TO_DATE':
      return getInterval(getStartOfThisYear(now), getEndOfToday(now));
    case 'THIS_YEAR':
      return getInterval(getStartOfThisYear(now), getEndOfThisYear(now));
    case 'RECENT_30_DAYS':
      return getInterval(getStartOfDayFromToday(now, -29), getStartOfToday(now));
    case '30_DAYS':
      return getInterval(getStartOfDayFromToday(now, -30), getStartOfToday(now));
    case '60_DAYS':
      return getInterval(getStartOfDayFromToday(now, -60), getStartOfToday(now));
    case '90_DAYS':
      return getInterval(getStartOfDayFromToday(now, -90), getStartOfToday(now));
    case '365_DAYS':
      return getInterval(getStartOfDayFromToday(now, -365), getStartOfToday(now));
    case 'NEXT_365_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 365));
    case 'RECENT_2_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -1), getStartOfToday(now));
    case 'RECENT_3_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -2), getStartOfToday(now));
    case 'RECENT_6_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -5), getStartOfToday(now));
    case 'LAST_7_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -6), getStartOfToday(now));
    case 'RECENT_12_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -11), getStartOfToday(now));
    case 'LAST_24_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -23), getStartOfToday(now));
    case 'LAST_MONTH':
      return getInterval(getStartOfMonthFromThisMonth(now, -1), getStartOfThisMonth(now).minus({ days: 1 }));
    case 'LAST_YEAR':
      return getInterval(getStartOfYearFromThisYear(now, -1), getStartOfThisYear(now).minus({ days: 1 }));
    case 'ALL_DATES': // Only supported by generated reports for now
    case 'RECENT_5_YEARS':
      return getInterval(getStartOfYearFromThisYear(now, -4), getStartOfToday(now));
    case 'NEXT_7_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 7));
    case 'NEXT_14_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 14));
    case 'NEXT_MONTH':
      return getInterval(getStartOfMonthFromThisMonth(now, 1), getEndOfMonthFromThisMonth(now, 1));
    case 'NEXT_30_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 30));
    case 'NEXT_60_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 60));
    case 'THIS_QUARTER':
      return getInterval(getStartOfQuarter(now), getEndOfQuarter(now));
    case 'LAST_QUARTER':
      return getInterval(getStartOfQuarterFromThisQuarter(now, -1), getEndOfQuarterFromThisQuarter(now, -1));
    case 'NEXT_QUARTER':
      return getInterval(getStartOfQuarterFromThisQuarter(now, 1), getEndOfQuarterFromThisQuarter(now, 1));
    case 'QUARTER_TO_DATE':
      return getInterval(getStartOfQuarter(now), getEndOfToday(now));
    case '6_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -5), getEndOfThisMonth(now));
    case '12_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -11), getEndOfThisMonth(now));
    // Not compatible, but used by web first
    case '3_MONTHS_PRIOR':
      return getInterval(getStartOfMonthFromThisMonth(now, -3), getStartOfThisMonth(now));
    case '6_MONTHS_PRIOR':
      return getInterval(getStartOfMonthFromThisMonth(now, -6), getStartOfThisMonth(now));
    case '12_MONTHS_PRIOR':
      return getInterval(getStartOfMonthFromThisMonth(now, -12), getStartOfThisMonth(now));
    case 'SAME_DAY_LAST_MONTH':
      return getInterval(getStartOfDayFromToday(now, -1, 'months'), getStartOfToday(now));
    case 'SAME_DAY_LAST_3_MONTHS':
      return getInterval(getStartOfDayFromToday(now, -3, 'months'), getStartOfToday(now));
    case 'SAME_DAY_LAST_6_MONTHS':
      return getInterval(getStartOfDayFromToday(now, -6, 'months'), getStartOfToday(now));
    case 'SAME_DAY_LAST_YEAR':
      return getInterval(getStartOfDayFromToday(now, -1, 'years'), getStartOfToday(now));
    case 'SAME_DAY_LAST_5_YEARS':
      return getInterval(getStartOfDayFromToday(now, -5, 'years'), getStartOfToday(now));
  }
};

// Sunday-based week helpers. Luxon's ISO weekday is 1=Mon…7=Sun; weekday % 7 gives days-since-Sunday (0 on Sun).
export const startOfSundayWeek = (dt: DateTime): DateTime => dt.minus({ days: dt.weekday % 7 }).startOf('day');
export const endOfSundayWeek = (dt: DateTime): DateTime => startOfSundayWeek(dt).plus({ days: 6 }).endOf('day');

// The dynamic ("auto-updating") ranges. These resolve to concrete dates relative to `now`, unlike the
// predefined ranges above they carry their own parameters (a start date, or an interval + count).
export type DynamicRange =
  | { type: 'CUSTOM_SINCE'; startDate: DateTime }
  | { type: 'CUSTOM_LAST'; interval: DynamicRangeIntervalTimestep; count: number }
  | { type: 'CUSTOM_RECENT'; interval: DynamicRangeIntervalTimestep; count: number };

/**
 * Resolve a dynamic range into a concrete interval relative to `now`.
 *
 * - `CUSTOM_SINCE`: a fixed start date through now.
 * - `CUSTOM_LAST`: the last `count` completed units, excluding the current in-progress unit
 *   (e.g. "last 3 months" on Jul 9 → Apr 1 through Jun 30).
 * - `CUSTOM_RECENT`: from the start of the earliest of `count` periods (including the current,
 *   in-progress one) through now (e.g. "recent 2 months" on Jul 9 → Jun 1 through Jul 9).
 */
export const constructDynamicInterval = (range: DynamicRange, now: DateTime): Interval => {
  switch (range.type) {
    case 'CUSTOM_SINCE':
      return Interval.fromDateTimes(range.startDate, now);
    case 'CUSTOM_LAST': {
      const unit = range.interval.toLowerCase() as DateTimeUnit;
      if (unit === 'week') {
        const currentWeekStart = startOfSundayWeek(now);
        return Interval.fromDateTimes(
          currentWeekStart.minus({ weeks: range.count }),
          endOfSundayWeek(currentWeekStart.minus({ weeks: 1 })),
        );
      }
      const currentUnitStart = now.startOf(unit);
      return Interval.fromDateTimes(
        currentUnitStart.minus({ [`${unit}s`]: range.count } as Record<string, number>),
        currentUnitStart.minus({ [`${unit}s`]: 1 } as Record<string, number>).endOf(unit),
      );
    }
    case 'CUSTOM_RECENT': {
      const unit = range.interval.toLowerCase() as DateTimeUnit;
      const start =
        unit === 'week'
          ? startOfSundayWeek(now).minus({ weeks: range.count - 1 })
          : now.minus({ [`${unit}s`]: range.count - 1 } as Record<string, number>).startOf(unit);
      return Interval.fromDateTimes(start, now);
    }
  }
};

type PredefinedInterval = { value: string; endDate?: DateTime; startDate?: DateTime };

export const intervalForRange = (range: PredefinedInterval, now: DateTime) => {
  switch (range.value?.toLowerCase()) {
    // Investment ranges - start
    case '30 days':
      return constructPredefinedInterval({ value: '30_DAYS' }, now);
    case '60 days':
      return constructPredefinedInterval({ value: '60_DAYS' }, now);
    case '90 days':
      return constructPredefinedInterval({ value: '90_DAYS' }, now);
    case '6 months':
      return constructPredefinedInterval({ value: '6_MONTHS' }, now);
    case '365 days':
      return constructPredefinedInterval({ value: '365_DAYS' }, now);
    case 'next 12 months':
      return constructPredefinedInterval({ value: 'NEXT_365_DAYS' }, now);
    case 'last year':
      return constructPredefinedInterval({ value: 'LAST_YEAR' }, now);
    case 'this year':
      return constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, now);
    // Investment ranges - end
    case 'last 30 days':
      return constructPredefinedInterval({ value: 'RECENT_30_DAYS' }, now);
    case 'month to date':
      return constructPredefinedInterval({ value: 'MONTH_TO_DATE' }, now);
    case 'next 7 days':
      return constructPredefinedInterval({ value: 'NEXT_7_DAYS' }, now);
    case 'next 14 days':
      return constructPredefinedInterval({ value: 'NEXT_14_DAYS' }, now);
    case 'this month':
      return constructPredefinedInterval({ value: 'THIS_MONTH' }, now);
    case 'next month':
      return constructPredefinedInterval({ value: 'NEXT_MONTH' }, now);
    case 'next 30 days':
      return constructPredefinedInterval({ value: 'NEXT_30_DAYS' }, now);
    case 'next 60 days':
      return constructPredefinedInterval({ value: 'NEXT_60_DAYS' }, now);
    case 'this quarter':
      return constructPredefinedInterval({ value: 'THIS_QUARTER' }, now);
    case 'next quarter':
      return constructPredefinedInterval({ value: 'NEXT_QUARTER' }, now);
    case 'last month':
      return constructPredefinedInterval({ value: 'LAST_MONTH' }, now);
    case 'last 2 months':
      return constructPredefinedInterval({ value: 'RECENT_2_MONTHS' }, now);
    case 'last 3 months':
      return constructPredefinedInterval({ value: 'RECENT_3_MONTHS' }, now);
    case 'last 6 months':
      return constructPredefinedInterval({ value: 'RECENT_6_MONTHS' }, now);
    case 'last 7 months':
      return constructPredefinedInterval({ value: 'LAST_7_MONTHS' }, now);
    case 'last 12 months':
      return constructPredefinedInterval({ value: 'RECENT_12_MONTHS' }, now);
    case 'last 24 months':
      return constructPredefinedInterval({ value: 'LAST_24_MONTHS' }, now);
    case 'year to date':
      return constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, now);
    case 'custom': {
      if (range.endDate && range.startDate) {
        return constructPredefinedInterval(
          { endDate: range.endDate, startDate: range.startDate, value: 'CUSTOM' },
          now,
        );
      }
      return constructPredefinedInterval({ value: 'RECENT_30_DAYS' }, now);
    }
    default:
      return constructPredefinedInterval({ value: 'RECENT_30_DAYS' }, now);
  }
};

// For Reference, Date Settings_Unused Supported by QMAC & QWIN

// This Month       - Start of Current Month to Start of Current Month + 1
// This Quarter     - Start of Current Quarter to Start of Current Quarter + 1
// This Year        - Start of Current Year to Start of Current Year + 1
//
// Last Week (Windows Only)
// Last Month       - Start of Current Month - 1 to Start of Current Month
// Last Quarter     - Start  of Current Quarter - 1 to Start of Current Quarter
// Last Year        - Start of Current Year - 1 to Start of Current Year
//

// Week to Date (Windows Only)
// Month to Date                       - Start of Current Month - Start of Today + 1
// Quarter to Date                     - Start of Current Quarter - Start of Today + 1
// Year to Date                        - Start of Current Year - Start of Today + 1
// Earliest to Date (Windows Only)     - Epoch to Start of Today + 1
// Custom to Date (Windows Only)

// Last 7 Days                         - Start of Current Day - 6 -> Start of Today + 1
// Last 14 Days (MAC Only)             - Start of Current Day - 13 -> Start of Today + 1
// Last 30 Days                        - Start of Current Day - 29 -> Start of Today + 1
// Last 60 Days (MAC Only)             - Start of Current Day - 60 -> Start of Today + 1
// Last 90 Days (MAC Only)             - Start of Current Day - 90 -> Start of Today + 1
// Last 12 Months                      - Start of Current Day - (12 months) -> Start of Today + 1

// Custom Range

// Daily              - Windows has bug, only shows today and last two days (assume it should show current and past 30 days
// Monthly            - Windows shows current month and past 12 months
// Yearly             - Windows shows current year and past N years

// SAME_DAY_LAST_MONTH              - Start of Day 1 month prior (keeping same calendar day)
// SAME_DAY_LAST_3_MONTHS           - Start of Day 3 months prior (keeping same calendar day)
// SAME_DAY_LAST_6_MONTHS           - Start of Day 6 months prior (keeping same calendar day)
// SAME_DAY_LAST_YEAR               - Start of Day 1 year prior (keeping same calendar day)
// SAME_DAY_LAST_5_YEARS            - Start of Day 5 years prior (keeping same calendar day)
